import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Search, Heart, User, ChefHat, Sparkles } from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import useAuthStore from '../../store/auth'
import useRecipeStore from '../../store/recipes'
import { cn } from '../../lib/utils'

const Header = () => {
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const { isAuthenticated, user, logout } = useAuthStore()
  const { setFilters } = useRecipeStore()

  const handleSearch = (e) => {
    setFilters({ searchQuery: e.target.value })
  }

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-white/20 shadow-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <motion.div
              whileHover={{ rotate: 10, scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400 }}
              className="p-2 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl shadow-playful"
            >
              <ChefHat className="w-6 h-6 text-white" />
            </motion.div>
            <div className="flex flex-col">
              <span className="text-2xl font-display font-bold text-dreamy">
                Recipy
              </span>
              <span className="text-xs font-playful text-gray-500 -mt-1">
                Childhood Dreams
              </span>
            </div>
          </Link>

          {/* Search Bar */}
          <div className="flex-1 max-w-lg mx-8">
            <div className={cn(
              "relative transition-all duration-300",
              isSearchFocused && "scale-105"
            )}>
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search magical recipes..."
                className="pl-10 pr-4 bg-white/90"
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                onChange={handleSearch}
              />
              {isSearchFocused && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  <Sparkles className="w-4 h-4 text-primary-500 animate-sparkle" />
                </motion.div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-4">
            <Link to="/recipes" className="nav-link">
              Recipes
            </Link>
            <Link to="/create" className="nav-link">
              Create
            </Link>
            <Link to="/favorites" className="nav-link flex items-center space-x-1">
              <Heart className="w-4 h-4" />
              <span>Favorites</span>
            </Link>
          </nav>

          {/* User Actions */}
          <div className="flex items-center space-x-3">
            {isAuthenticated ? (
              <div className="flex items-center space-x-3">
                <Button variant="ghost" size="icon" asChild>
                  <Link to="/profile">
                    <User className="w-5 h-5" />
                  </Link>
                </Button>
                <div className="flex items-center space-x-2">
                  <img
                    src={user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=f59e0b&color=fff`}
                    alt={user?.name}
                    className="w-8 h-8 rounded-full border-2 border-white shadow-soft"
                  />
                  <span className="text-sm font-medium text-gray-700 hidden sm:block">
                    {user?.name}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={logout}
                  className="hidden sm:flex"
                >
                  Logout
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Button variant="ghost" asChild>
                  <Link to="/login">Sign In</Link>
                </Button>
                <Button asChild animation="sparkle">
                  <Link to="/register">Join Magic</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden px-4 py-2 border-t border-white/20">
        <nav className="flex items-center justify-around">
          <Link to="/recipes" className="nav-link text-sm">
            Recipes
          </Link>
          <Link to="/create" className="nav-link text-sm">
            Create
          </Link>
          <Link to="/favorites" className="nav-link text-sm flex items-center space-x-1">
            <Heart className="w-4 h-4" />
            <span>Favorites</span>
          </Link>
        </nav>
      </div>
    </header>
  )
}

export default Header